import { Timestamp } from "./common";
import { CampaignType } from "./campaign";
import type { SelectedProduct } from "./campaign";
import { ContentRequirement, ContentSubmissionUnit } from "./content";
import { DeliverableLink } from "./deliverable";
import { FoodfluenceLocation } from "./foodfluenceLocation";

export type CollabStatus = 
  'pendingInfluencerApproval' | 
  'pendingClientApproval' |
  'influencerCountered' |
  'pendingShipment' |
  'pendingSubmission' | 
  'pendingResubmission' | 
  'pendingVerification' | 
  'collabRejected' | 
  'collabCanceled' |
  'collabRequestCanceled' |
  'pendingRedemption' | 
  'collabRedeemed' |
  'collabCompleted' |
  'collabExpired' |
  'pendingDraftSubmission' |
  'clientRejected' |
  'clientCanceled';

/** Who made a change: `<kind>:<id>`, or legacy `'sienna'`. Mirrors the dashboard's shared type. */
export type CollabActor = 'sienna' | `${'brand' | 'staff' | 'agent' | 'system' | 'creator'}:${string}`;

export interface CollabLog {
  status: CollabStatus;
  timestamp: Timestamp;
  /** Absent on old entries: unknown, never "brand". */
  source?: CollabActor;
}

/** One offer amount sent on a collab, by either side. Mirrors the dashboard's shared type. */
export interface CollabOffer {
  cash: number;
  credit: number;
  timestamp: Timestamp;
  actor: CollabActor;
}

export interface ScheduleLog {
  // 'agent_reschedule' is written when the dashboard inbox rescheduling agent moves the
  // visit date. It is intentionally NOT 'influencer_reschedule', so it does not count
  // toward the creator's reschedule cap (see countRescheduleAttempts in rescheduleUtils).
  action: 'influencer_schedule' | 'influencer_reschedule' | 'client_reschedule' | 'agent_reschedule';
  timestamp: Timestamp;
  previousDate?: Timestamp;
  newDate: Timestamp;
}

export interface CollabCounter {
  reason: string | string[];
  timestamp: Timestamp;
  proposedInfluencerDeliverableCounts?: Record<string, number>;
  proposedInfluencerCash?: number;
  proposedInfluencerCredit?: number;
  /** UGC collabs: the content terms the creator proposed in this round. */
  proposedContent?: Record<string, ContentRequirement>;
  /** Optional free-text note the creator attached to this counter. */
  note?: string;
}

/**
 * A brand ("client") counter-back, appended to `Collab.clientCounters` when the brand counters the
 * creator's terms. Shape written by the dashboard backend's counterRequest flow.
 */
export interface ClientCollabCounter {
  deliverableCounts?: Record<string, number>;
  cash: number;
  credit: number;
  timestamp: Timestamp;
  /** UGC collabs: the content terms the brand countered with in this round. */
  contentRequirements?: Record<string, ContentRequirement>;
  /** Optional free-text note the brand attached to this counter-back. */
  note?: string;
}

export interface Collab {
  id: string;
  credit: number;
  cash: number;
  /** Product snapshot copied from the campaign when the invite was created. */
  productName?: string;
  productValue?: number;
  /**
   * What this creator chose, recorded when they accepted. Snapshots, not references — later edits
   * to the campaign or the brand's catalog do not change what this collab says was sent. Read
   * through `resolveCollabProductValue`, never directly.
   */
  selectedProducts?: SelectedProduct[];
  deliverableCounts: Record<string, number>;
  deliverableLinks?: Record<string, DeliverableLink[]>;
  status: CollabStatus;
  rejectionReason?: string;
  clientCancelationReason?: string;
  influencerID: string;
  influencerCampaignID?: string | null;
  userConfirmRedemption?: string;
  collabReceivedTime?: Timestamp;
  collabOfferFirstReviewedTime?: Timestamp;
  collabOfferExpiresAt?: Timestamp;
  collabAcceptedTime?: Timestamp;
  collabConfirmedTime?: Timestamp;
  collabVerifiedTime?: Timestamp;
  collabRedeemedTime?: Timestamp;
  /**
   * When the collab was canceled. Written by both cancel paths — the creator's own
   * /collabs/cancel and the inbox agent's internal agent-cancel — and read by the creator inbox
   * to decide whether the Undo chip is still inside CREATOR_UNDO_WINDOW_MS.
   *
   * Declared late: the backend has always written it, but this type never said so, so any
   * frontend read of it failed to compile. Mirrors the dashboard's shared Collab.
   */
  collabCanceledTime?: Timestamp;
  collabCounteredTime?: Timestamp;
  scheduledCollabDate?: Timestamp;
  orderFulfilledTime?: Timestamp;
  /**
   * When the carrier delivered the whole Shopify gifting order, from Flask's scheduled delivery sync
   * (mirrors the dashboard). Never set for untracked shipments.
   */
  orderDeliveredTime?: Timestamp;
  paymentClaimed?: boolean;
  /** When creator payout was transferred to Whop (or marked settled with no cash). */
  paymentClaimedAt?: Timestamp;
  /** Whop transfer id when returned by the API; omitted on idempotent replay. Idempotence key is collab_transfer_{id}. */
  whopTransferId?: string;
  /** How the brand paid. 'stripe' = paid via Stripe, 'whop' = paid via Whop, null/undefined = legacy (treat as Stripe). */
  paymentSource?: 'stripe' | 'whop' | 'manual' | 'other';
  selectedLocation?: string;
  shippingAddress?: string;
  proposedInfluencerDeliverableCounts?: Record<string, number>;
  proposedInfluencerCash?: number;
  proposedInfluencerCredit?: number;
  counters?: CollabCounter[];
  /** Every offer amount sent, oldest first. Absent on collabs from before it was recorded. */
  offers?: CollabOffer[];
  /** The brand's counter-backs (mirror of `counters`, which holds the creator's counters). */
  clientCounters?: ClientCollabCounter[];
  businessRejectedCounter?: boolean;
  /** Optional free-text note explaining the creator's current counter (shown to the brand). */
  influencerCounterNote?: string | null;
  /** Optional free-text note explaining the brand's current counter-back (shown to the creator). */
  clientCounterNote?: string | null;
  partySize?: number;
  influencerNote?: string | null;
  customCollabType?: CampaignType;
  /** Explicit discriminant for UGC collabs (custom UGC proposals and UGC campaign requests). */
  creatorType?: 'ugc' | 'influencer';
  /**
   * UGC collabs: the CREATOR's current content offer (written on request/counter), mirroring
   * proposedInfluencerDeliverableCounts. The brand's offer lives in contentRequirements/content;
   * accepting copies proposedContent into them.
   */
  proposedContent?: Record<string, ContentRequirement>;
  collabRequestedTime?: Timestamp;
  proposedSelectedLocationIds?: string[];
  logs: CollabLog[];
  scheduleLogs?: ScheduleLog[];
  clientID?: string;
  /** How this collab originated (mirrors the dashboard); 'inboundLink' = creator applied via the brand's shared Creator Link. */
  source?: 'brandRec' | 'direct' | 'inboundLink';
  shopifyOrderID?: string;
  /**
   * The Shopify order IDs of brand-initiated extra shipments on a Shopify collab — extra items the
   * brand chose to send after the original order had already gone out — in the order they were
   * sent. Absent or empty on every collab that never had one.
   *
   * The original order stays in `shopifyOrderID`, is never overwritten, and is never part of this
   * list.
   *
   * Written only by `foodfluence_dashboard`'s backend, which is where the decision to spend real
   * inventory is authorised. This repo only reads it.
   */
  shopifyExtraShipmentOrderIDs?: string[];
  shopifyDiscountCode?: string;
  shopifyShippingAddressFallback?: ShopifyShippingAddressFallback;
  /** Latest carrier status of the Shopify gifting order; written by Flask (mirrors the dashboard). */
  shopifyShipment?: ShopifyShipment;
  clientRequestedReschedule?: boolean;
  /**
   * In-store: the visit date advanced by the creator's chosen turnaround — a brand-facing gauge
   * that does NOT drive the post deadline. Retail: the posting date the creator picked directly.
   * The turnaround itself is derived from this and `scheduledCollabDate`, never stored
   * (see deriveTurnaroundDays).
   */
  scheduledPostingDate?: string;
  inStorePreVisitCheckInTaskName?: string;
  inStorePostVisitCheckInTaskName?: string;
  // Chained check-in follow-up tasks (scheduled by the dashboard when a stage fires):
  inStorePreVisitFinalNoticeTaskName?: string;
  inStorePreVisitAutoCancelTaskName?: string;
  inStorePostContentFollowupTaskName?: string;
  inStorePostContentOverdueTaskName?: string;
  // Check-in response state machine:
  preVisitCheckInStatus?: PreVisitCheckInStatus;
  postVisitCheckInStatus?: PostVisitCheckInStatus;
  // Shopify arrival check-in (written by the dashboard; mirrors its Collab type)
  shopifyArrivalCheckInStatus?: ShopifyArrivalCheckInStatus;
  shopifyArrivalNotYetCount?: number;
  shopifyArrivalAskAgainAt?: Timestamp;
  shopifyArrivalFollowupTaskName?: string;
  shopifyArrivalResolveTaskName?: string;
  availableTimes?: AvailableTimes;
  contentRequirements?: Record<string, ContentRequirement>;
  content?: Record<string, ContentSubmissionUnit>;
  endDateExtensionRequestTime?: Timestamp;
  waitlistRequestTime?: Timestamp;
  endDate?: Timestamp;
  contentDueDate?: Timestamp;
  /** Turnaround locked in at agreement. Absent on legacy collabs, which never get reminders re-armed. */
  contentTurnaroundDays?: number;
  /** Campaign go-live window ('YYYY-MM-DD', start = end for a specific date) locked at acceptance. Absent = no go-live. */
  contentGoLiveStart?: string;
  contentGoLiveEnd?: string;
  /** What contentDueDate means for this collab, locked at acceptance. Absent on older collabs: read via deadlineKindFor. */
  contentDeadlineKind?: ContentDeadlineKind;
  /** When the creator confirmed they can post inside the go-live window. */
  goLiveConfirmedAt?: Timestamp;
  /** Total days the brand has extended contentDueDate by (capped at MAX_DUE_DATE_EXTENSION_DAYS). */
  contentDueDateExtensionDays?: number;
  /**
   * When the brand approved a per-collab extension (written by the dashboard). A contentDueDate on a
   * not-yet-accepted collab counts only with this set (see preAcceptExtendedDueDate).
   */
  contentDueDateExtendedAt?: Timestamp;
  /** When the creator received the product (ship-to-home); the due-date clock starts here. */
  productReceivedTime?: Timestamp;
  /** Ship-to-home: when the brand confirmed shipment (written by the dashboard). With productReceivedTime, starts the content clock. */
  shippingConfirmed?: Timestamp;
  /** Set when re-arming content reminders after a due-date change failed. */
  contentReminderSyncStatus?: 'failed' | 'stuck';
}

/** Stages of the in-store check-in pacing flow (mirrors the dashboard). */
export type InStoreCheckInStage =
  | 'preVisitInitial'
  | 'preVisitFinalNotice'
  | 'preVisitAutoCancel'
  | 'postVisitInitial'
  | 'postContentFollowup'
  | 'postContentOverdue'
  // Shopify gifting "did our package arrive?" chain (mirrors the dashboard; see it for the flow).
  | 'shopifyArrivalInitial'
  | 'shopifyArrivalFollowup'
  | 'shopifyArrivalResolve';

export type PreVisitCheckInStatus =
  | 'sent'
  | 'confirmed'
  | 'reschedule'
  | 'nudged'
  | 'canceled'
  | 'autoCanceled';

export type PostVisitCheckInStatus =
  | 'sent'
  | 'responded'
  | 'followedUp'
  | 'overdue';

/** Mirrors the dashboard's ShopifyArrivalCheckInStatus. */
export type ShopifyArrivalCheckInStatus =
  | 'sent'
  | 'followedUp'
  | 'notYet'
  | 'received'
  | 'autoReceived'
  | 'problem'
  | 'escalated'
  | 'responded';

/** Action a check-in quick-reply chip triggers when tapped. */
export type CheckInResponseAction =
  | 'confirm' | 'reschedule' | 'cancel' | 'alreadyVisited'
  // Shopify arrival check-in chips: "Yes, got it!" / "Not yet" / "Something's wrong"
  | 'packageReceived' | 'packageNotYet' | 'packageProblem';

/** 'draft' = contentDueDate is when drafts are due (brand reviews first); 'post' = when content goes live. */
export type ContentDeadlineKind = 'draft' | 'post';

export interface CheckInMessageOption {
  action: CheckInResponseAction;
  label: string;
}

/**
 * Booking increment: the spacing between the start times a brand opens.
 *
 * Must stay in sync with the copy in foodfluence_dashboard's shared package — the dashboard
 * writes these values, this repo expands ranges with them. Every value divides 60, which is
 * what lets a range's edges stay on the hour and still line up with a midnight-anchored grid.
 */
export const BOOKING_INCREMENT_OPTIONS = [60, 30, 15] as const;
export type BookingIncrementMinutes = (typeof BOOKING_INCREMENT_OPTIONS)[number];

/** Applied to any range with no increment of its own (saved before increments shipped). */
export const DEFAULT_BOOKING_INCREMENT: BookingIncrementMinutes = 30;

export interface TimeSlot {
  startTime: string;
  /** Exclusive: no bookable start is generated at this time. */
  endTime: string;
  /**
   * Spacing of the bookable start times inside this range. Absent on ranges saved before
   * increments shipped — read as {@link DEFAULT_BOOKING_INCREMENT}.
   */
  incrementMinutes?: BookingIncrementMinutes;
}

export type AvailableTimes = Record<string, TimeSlot[]>;

export interface ShopifyShippingAddressFallback {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/** Mirrors the dashboard's ShopifyShipment; see it for field semantics. */
export interface ShopifyShipment {
  displayStatus?: string;
  trackingCompany?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  fulfilledAt?: Timestamp;
  inTransitAt?: Timestamp;
  estimatedDeliveryAt?: Timestamp;
  deliveredAt?: Timestamp;
  fulfillments: ShopifyShipmentFulfillment[];
  updatedAt: Timestamp;
}

export interface ShopifyShipmentFulfillment {
  fulfillmentId?: string;
  status?: string;
  displayStatus?: string;
  trackingCompany?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  fulfilledAt?: Timestamp;
  inTransitAt?: Timestamp;
  estimatedDeliveryAt?: Timestamp;
  deliveredAt?: Timestamp;
}

// Home page card UI types
export type CtaVariant = 'green' | 'red' | 'gray' | 'arrow' | 'spots';

export interface CardCta {
  label: string;
  variant: CtaVariant;
  /** When variant is 'spots', used for color (green/orange/red) and text contrast */
  spotsCount?: number;
}

export interface CollabTask {
  collab: Collab;
  title: string;
  dueDate?: Date | null;
  ctaLabel?: string;
}
